import { useState, useEffect } from "react";
import { userAPI, authAPI } from "../../utils/api";
import { Avatar, Toast } from "../../components/shared/index.jsx";
import { useAuth } from "../../hooks/useAuth";
import { formatMinutes } from "../../utils/helpers";
import { TrashIcon, KeyIcon } from "../../components/shared/Icons.jsx";

// ─── Top-level components (stable identity across renders) ────────────────
// Moved OUTSIDE SupervisorsPage. Previously these were declared inside the
// component body, which meant React created a brand-new component type on
// every render (e.g. every keystroke in a form). That caused React to
// unmount/remount the whole subtree -- including <input> elements -- so
// inputs lost focus after every single character typed.

function Modal({ children, onBgClick }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16 }}
      onClick={e => e.target === e.currentTarget && onBgClick()}>
      <div style={{ background:"white", borderRadius:18, maxWidth:"100%", maxHeight:"90vh", overflow:"auto", padding:"32px 28px", boxShadow:"0 24px 80px rgba(0,0,0,.3)" }}>
        {children}
      </div>
    </div>
  );
}

function getRoleBadge(sv) {
  if (sv.supervisorLevel === "senior") return { label:"Senior Supervisor", bg:"#faf5ff", color:"#7c3aed", border:"#ddd6fe" };
  if (sv.supervisorLevel === "junior") return { label:"Junior Supervisor", bg:"#fff7ed", color:"#c2410c", border:"#fed7aa" };
  return                                      { label:"Supervisor",        bg:"#f0fdf4", color:"#166534", border:"#bbf7d0" };
}

function SupervisorCard({ sv, isSenior, onDelete, onResetPassword }) {
  const badge = getRoleBadge(sv);
  return (
    <div className="intern-card">
      <div className="flex items-center gap-12 mb-16">
        <Avatar initials={sv.avatar} color={sv.avatarColor} size="lg"/>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:"Syne,sans-serif", fontWeight:800, fontSize:15, color:"var(--green-900)" }}>{sv.name}</div>
          <div style={{ fontSize:12, fontWeight:600, marginTop:3 }}>
            <span style={{ padding:"2px 10px", borderRadius:99, fontSize:11, fontWeight:700, background:badge.bg, color:badge.color, border:`1px solid ${badge.border}` }}>
              {badge.label}
            </span>
          </div>
        </div>
        {isSenior && sv.supervisorLevel !== "senior" && (
          <button onClick={() => onDelete(sv)} style={{ background:"none", border:"none", cursor:"pointer", padding:"4px", display:"flex", alignItems:"center" }} title="Remove supervisor">
            <TrashIcon size={16} color="#ef4444" />
          </button>
        )}
      </div>

      <div style={{ fontSize:12, color:"var(--gray-500)", marginBottom:12, display:"flex", flexDirection:"column", gap:6 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
          {sv.email}
        </div>
        {sv.contact && (
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            {sv.contact}
          </div>
        )}
        {sv.position && (
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            {sv.position}
          </div>
        )}
        {sv.department && (
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M8 10h.01"/><path d="M16 10h.01"/><path d="M8 14h.01"/><path d="M16 14h.01"/></svg>
            {sv.department}
          </div>
        )}
      </div>

      {isSenior && (
        <button onClick={() => onResetPassword(sv)}
          style={{ marginTop:8, width:"100%", padding:"7px", borderRadius:8, border:"1px solid #fecaca", background:"#fef2f2", color:"#dc2626", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
          <KeyIcon size={13} color="#dc2626" /> Reset Password
        </button>
      )}
    </div>
  );
}

function Section({ title, list, isSenior, onDelete, onResetPassword }) {
  if (list.length === 0) return null;
  return (
    <>
      <div style={{ fontSize:11, fontWeight:700, color:"var(--gray-400)", letterSpacing:".08em", textTransform:"uppercase", marginBottom:12, marginTop:24 }}>
        {title} ({list.length})
      </div>
      <div className="grid-3">
        {list.map(sv => (
          <SupervisorCard key={sv._id} sv={sv} isSenior={isSenior} onDelete={onDelete} onResetPassword={onResetPassword} />
        ))}
      </div>
    </>
  );
}

export function SupervisorsPage() {
  const { user, isSenior } = useAuth();
  const [supervisors,      setSupervisors]      = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [toast,            setToast]            = useState(null);
  const [showAdd,          setShowAdd]          = useState(false);
  const [adding,           setAdding]           = useState(false);
  const [deleteTarget,     setDeleteTarget]     = useState(null);
  const [deleting,         setDeleting]         = useState(false);
  const [resetTarget,      setResetTarget]      = useState(null);
  const [newPass,          setNewPass]          = useState("");
  const [resetting,        setResetting]        = useState(false);
  const [form, setForm] = useState({
    name:"", username:"", password:"", email:"",
    contact:"", position:"", department:"", supervisorLevel:"supervisor",
  });

  const load = () => {
    setLoading(true);
    userAPI.getAll("supervisor")
      .then(d => {
        const others = (d.users || []).filter(u => String(u._id) !== String(user._id || user.id));
        setSupervisors(others);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.name || !form.username || !form.password || !form.email) {
      setToast({ msg:"Name, username, password and email are required.", type:"error" }); return;
    }
    setAdding(true);
    try {
      await authAPI.registerSupervisor(form);
      setToast({ msg:`${form.name} added successfully!`, type:"success" });
      setShowAdd(false);
      setForm({ name:"", username:"", password:"", email:"", contact:"", position:"", department:"", supervisorLevel:"supervisor" });
      load();
    } catch (e) { setToast({ msg: e.message, type:"error" }); }
    setAdding(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await authAPI.deleteSupervisor(deleteTarget._id);
      setSupervisors(s => s.filter(u => u._id !== deleteTarget._id));
      setToast({ msg:`${deleteTarget.name} removed.`, type:"success" });
      setDeleteTarget(null);
    } catch (e) { setToast({ msg: e.message, type:"error" }); }
    setDeleting(false);
  };

  const handleResetPassword = async () => {
    if (!newPass || newPass.length < 6) { setToast({ msg:"Password must be at least 6 characters.", type:"error" }); return; }
    setResetting(true);
    try {
      await authAPI.resetPassword(resetTarget._id, { newPassword: newPass });
      setToast({ msg:`Password reset for ${resetTarget.name}.`, type:"success" });
      setResetTarget(null); setNewPass("");
    } catch (e) { setToast({ msg: e.message, type:"error" }); }
    setResetting(false);
  };

  if (loading) return <div style={{ padding:60, textAlign:"center" }}><div className="spinner"/></div>;

  const juniors   = supervisors.filter(s => s.supervisorLevel === "junior");
  const plains    = supervisors.filter(s => s.supervisorLevel === "supervisor");
  const seniors   = supervisors.filter(s => s.supervisorLevel === "senior");

  return (
    <div className="animate-fadeUp">
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)}/>}

      {/* Add Supervisor Modal */}
      {showAdd && (
        <Modal onBgClick={() => setShowAdd(false)}>
          <div style={{ width:500 }}>
            <div style={{ fontFamily:"Syne,sans-serif", fontWeight:800, fontSize:18, color:"#0a2e1a", marginBottom:6 }}>➕ Add Supervisor</div>
            <p style={{ fontSize:13, color:"#6b7280", marginBottom:20 }}>Fill in the details to register a new supervisor.</p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {[
                ["Full Name *",  "name",     "text"],
                ["Username *",   "username", "text"],
                ["Password *",   "password", "password"],
                ["Email *",      "email",    "email"],
                ["Contact",      "contact",  "text"],
                ["Position",     "position", "text"],
                ["Department",   "department","text"],
              ].map(([label, key, type]) => (
                <div key={key} style={{ gridColumn: key==="name" ? "1/-1" : "auto" }}>
                  <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>{label}</label>
                  <input
                    type={type}
                    className="form-input"
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  />
                </div>
              ))}
              <div style={{ gridColumn:"1/-1" }}>
                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Role *</label>
                <div style={{ display:"flex", gap:10 }}>
                  {[
                    { value:"supervisor", label:"Supervisor",        bg:"#f0fdf4", color:"#166534", border:"#bbf7d0" },
                    { value:"junior",     label:"Junior Supervisor",  bg:"#fff7ed", color:"#c2410c", border:"#fed7aa" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setForm(f => ({ ...f, supervisorLevel: opt.value }))}
                      style={{
                        flex:1, padding:"10px 14px", borderRadius:10, cursor:"pointer", fontFamily:"inherit",
                        border: form.supervisorLevel === opt.value ? `2px solid ${opt.border}` : "2px solid #e5e7eb",
                        background: form.supervisorLevel === opt.value ? opt.bg : "#f9fafb",
                        color: form.supervisorLevel === opt.value ? opt.color : "#6b7280",
                        fontWeight: form.supervisorLevel === opt.value ? 700 : 500,
                        fontSize:13, transition:"all .15s",
                      }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:20 }}>
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={adding}>{adding ? "Adding…" : "Add Supervisor"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <Modal onBgClick={() => setResetTarget(null)}>
          <div style={{ width:360 }}>
            <div style={{ fontFamily:"Syne,sans-serif", fontWeight:800, fontSize:18, color:"#0a2e1a", marginBottom:6 }}>🔑 Reset Password</div>
            <p style={{ fontSize:13, color:"#6b7280", marginBottom:20 }}>New temporary password for <strong>{resetTarget.name}</strong>.</p>
            <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:5 }}>New Password</label>
            <input type="password" className="form-input" style={{ marginBottom:20 }} value={newPass}
              onChange={e => setNewPass(e.target.value)} placeholder="Min. 6 characters"
              onKeyDown={e => e.key==="Enter" && handleResetPassword()}/>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button className="btn btn-secondary" onClick={() => { setResetTarget(null); setNewPass(""); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResetPassword} disabled={resetting}>{resetting ? "Resetting…" : "Reset Password"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <Modal onBgClick={() => setDeleteTarget(null)}>
          <div style={{ width:360 }}>
            <div style={{ fontFamily:"Syne,sans-serif", fontWeight:800, fontSize:18, color:"#dc2626", marginBottom:6 }}>🗑 Remove Supervisor</div>
            <p style={{ fontSize:13, color:"#6b7280", marginBottom:20 }}>Remove <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button style={{ padding:"9px 20px", borderRadius:9, border:"none", background:"#ef4444", color:"white", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}
                onClick={handleDelete} disabled={deleting}>{deleting ? "Removing…" : "Remove"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Header */}
      {isSenior && (
        <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:20 }}>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Supervisor</button>
        </div>
      )}

      {supervisors.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🧑‍💼</div>
          <h3>No supervisors yet</h3>
          <p>Add a supervisor using the button above.</p>
        </div>
      ) : (
        <>
          <Section title="Junior Supervisors"  list={juniors} isSenior={isSenior} onDelete={setDeleteTarget} onResetPassword={setResetTarget}/>
          <Section title="Supervisors"         list={plains}  isSenior={isSenior} onDelete={setDeleteTarget} onResetPassword={setResetTarget}/>
          <Section title="Senior Supervisors"  list={seniors} isSenior={isSenior} onDelete={setDeleteTarget} onResetPassword={setResetTarget}/>
        </>
      )}
    </div>
  );
}

export default SupervisorsPage;